import { inject } from '@angular/core';
import { MetaDefinition } from '@angular/platform-browser';
import { ResolveFn } from '@angular/router';
import { ISbStoryData } from '@storyblok/js';
import { map, Observable, switchMap } from 'rxjs';
import { getMetaDescriptionTags, getMetaImageTags, getMetaTitleTags } from '../lib/meta-helpers';
import { PICFLOW_CONFIG } from '../picflow-config';
import { StoryblokService } from '../services/storyblok.service';
import { convertToOgImageUrl } from './lib/meta-helpers';
import { storyblokArticleStoryResolver } from './storyblok-article-story.resolver';
import { StoryblokImage } from './storyblok-types';

export const storyblokArticleMetaResolver: ResolveFn<unknown> = route => {
  const config = inject(PICFLOW_CONFIG);
  const storyblokService = inject(StoryblokService);

  const getTitle = (story: ISbStoryData): string => {
    if (story.content.metaTitle) {
      return story.content.metaTitle;
    }
    if (story.content.title) {
      return story.content.title;
    }
    return story.name;
  };

  const getDescription = (story: ISbStoryData): string => {
    if (story.content.metaDescription) return story.content.metaDescription;
    if (story.content.lead) {
      return story.content.lead;
    }
  };

  const getImageUrl = (story: ISbStoryData): string => {
    if (story.content.metaImage?.filename) {
      return story.content.metaImage.filename;
    }
    if (story.content.visual?.filename) {
      return story.content.visual.filename;
    }
  };

  const getAuthor = (story: ISbStoryData, relationships: ISbStoryData[]) => {
    const authorUuid = story.content.author;
    if (!authorUuid) return;
    return relationships.find(rel => rel.uuid === authorUuid)?.content as {
      name: string;
      image: StoryblokImage;
      instaLink: string;
      role: string;
      url: string;
      xLink: string;
    };
  };

  return (storyblokArticleStoryResolver(route) as Observable<false | ISbStoryData>).pipe(
    switchMap(story => {
      return storyblokService.resolveRelationships(route).pipe(map(relationships => ({ story, relationships })));
    }),
    map(({ story, relationships }) => {
      if (!story) return;
      const tags: MetaDefinition[] = [];

      const title = getTitle(story);
      const description = getDescription(story);
      const imageUrl = getImageUrl(story);
      const author = getAuthor(story, relationships);

      tags.push(...getMetaTitleTags(title));

      if (description) {
        tags.push(...getMetaDescriptionTags(description));
      }

      if (imageUrl) {
        tags.push(...getMetaImageTags(convertToOgImageUrl(imageUrl, config.storyblokUrl)));
      }

      if (author) {
        tags.push({ property: 'author', content: author.name });
      }

      tags.push({ property: 'og:type', content: 'article' });

      return { title, tags };
    })
  );
};
